import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronRight, Sparkles, Link2, X, Loader2, Undo2 } from "lucide-react";

const STATE_STYLE = {
  confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  derived: "bg-sky-50 text-sky-700 border-sky-200",
  suggested: "bg-amber-50 text-amber-700 border-amber-200",
  unknown: "bg-slate-50 text-slate-500 border-slate-200",
  hidden: "bg-slate-50 text-slate-400 border-slate-200",
};
const STATE_LABEL = { confirmed: "Confirmed", derived: "From records", suggested: "Needs checking", unknown: "Unknown", hidden: "Restricted" };
const EDITABLE = { salesperson: "select-people", agreed_date: "date", model: "select-products", agreed_price: "number" };

function FieldEditor({ field, options, onSave, onCancel }) {
  const [v, setV] = useState("");
  const kind = EDITABLE[field.key];
  const cls = "h-8 rounded-md border border-slate-300 px-2 text-sm bg-white min-w-0 flex-1";
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2" data-testid={`si-editor-${field.key}`}>
      {kind === "select-people" && (
        <select className={cls} value={v} onChange={(e) => setV(e.target.value)} data-testid={`si-input-${field.key}`}>
          <option value="">Choose team member…</option>
          {(options.people || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>)}
      {kind === "select-products" && (
        <select className={cls} value={v} onChange={(e) => setV(e.target.value)} data-testid={`si-input-${field.key}`}>
          <option value="">Choose catalogue product…</option>
          {(options.products || []).map((p) => <option key={p.legacy_id} value={p.legacy_id}>{p.title} · {p.category}</option>)}
        </select>)}
      {kind === "date" && <input type="date" className={cls} value={v} onChange={(e) => setV(e.target.value)} data-testid={`si-input-${field.key}`} />}
      {kind === "number" && <input type="number" min="0" step="0.01" placeholder="inc VAT" className={cls} value={v}
        onChange={(e) => setV(e.target.value)} data-testid={`si-input-${field.key}`} />}
      <Button size="sm" className="h-8 bg-slate-900 text-white" disabled={!v} onClick={() => onSave(field.key, v)} data-testid={`si-save-${field.key}`}>Save</Button>
      <Button size="sm" variant="ghost" className="h-8" onClick={onCancel} data-testid={`si-cancel-${field.key}`}>Cancel</Button>
    </div>
  );
}

function FieldRow({ field, canConfirm, editing, setEditing, options, onSave }) {
  return (
    <div className="py-2.5 border-b border-slate-100 last:border-0" data-testid={`si-field-${field.key}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="w-28 shrink-0 text-xs font-semibold text-slate-500">{field.label}</span>
        <span className="text-sm text-slate-900 font-medium min-w-0 break-words" data-testid={`si-value-${field.key}`}>
          {field.state === "hidden" ? (field.hidden_reason || "Restricted") : (field.value ?? "Unknown")}
        </span>
        <span className={`text-[10px] font-semibold uppercase tracking-wide border rounded-full px-2 py-0.5 ${STATE_STYLE[field.state]}`}
          data-testid={`si-state-${field.key}`}>{STATE_LABEL[field.state]}</span>
        {canConfirm && EDITABLE[field.key] && field.state !== "hidden" && editing !== field.key && (
          <button type="button" className="ml-auto text-xs text-indigo-600 hover:text-indigo-800" onClick={() => setEditing(field.key)}
            data-testid={`si-edit-${field.key}`}>{field.state === "confirmed" ? "Change" : "Confirm"}</button>)}
      </div>
      {field.basis && field.state !== "hidden" && <p className="text-[11px] text-slate-400 mt-0.5 sm:pl-[124px]">Source: {field.basis}</p>}
      {editing === field.key && <FieldEditor field={field} options={options} onSave={onSave} onCancel={() => setEditing(null)} />}
    </div>
  );
}

function Suggestions({ items, canConfirm, onDecide }) {
  if (!items.length) return null;
  return (
    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="si-install-suggestions">
      <p className="text-xs font-semibold text-amber-800">Possible installation jobs (suggested – unverified)</p>
      {items.map((s) => (
        <div key={s.job} className="flex flex-wrap items-center gap-2 mt-2 text-sm" data-testid={`si-suggestion-${s.job}`}>
          <a href={s.link} className="font-mono text-indigo-700 hover:underline">{s.job}</a>
          <span className="text-slate-700 truncate max-w-[16rem]">{s.title}</span>
          <span className="text-[11px] text-slate-500">{s.task_type} · {s.status} · {s.basis}</span>
          {canConfirm && (
            <span className="ml-auto flex gap-1">
              <Button size="sm" variant="outline" className="h-7" onClick={() => onDecide("install_link", s.job)} data-testid={`si-link-${s.job}`}>
                <Link2 className="w-3.5 h-3.5 mr-1" /> Link</Button>
              <Button size="sm" variant="ghost" className="h-7" onClick={() => onDecide("not_related", s.job)} data-testid={`si-notrelated-${s.job}`}>
                <X className="w-3.5 h-3.5 mr-1" /> Not related</Button>
            </span>)}
        </div>))}
    </div>
  );
}

function Confirmations({ items, canConfirm, onRevoke }) {
  if (!items.length) return null;
  return (
    <div className="mt-3" data-testid="si-confirmations">
      <p className="text-xs font-semibold text-slate-500">Confirmations (stored separately from this job)</p>
      {items.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-2 text-xs text-slate-600 mt-1" data-testid={`si-conf-${c.id}`}>
          <span className="font-medium text-slate-800">{c.field.replace(/_/g, " ")}: {c.display}</span>
          <span>by {c.by_name} · {String(c.at).slice(0, 10)}</span>
          {canConfirm && <button type="button" className="text-rose-600 hover:underline inline-flex items-center gap-0.5" onClick={() => onRevoke(c.id)}
            data-testid={`si-revoke-${c.id}`}><Undo2 className="w-3 h-3" /> Undo</button>}
        </div>))}
    </div>
  );
}

function useSalesIntel(legacyId, open) {
  const [data, setData] = useState(null);
  const [options, setOptions] = useState({});
  const load = () => api.get(`/sales-intel/jobs/${legacyId}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => {
    if (open && !data) { load(); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (data?.can_confirm && !options.people) {
      Promise.all([api.get("/sales-intel/people"), api.get("/sales-intel/products")])
        .then(([p, pr]) => setOptions({ people: p.data.people, products: pr.data.products })).catch(() => setOptions({ people: [], products: [] }));
    }
  }, [data, options.people]);
  return { data, setData, options, load };
}

export const SalesIntelCard = ({ legacyId, taskType }) => {
  const [on, setOn] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [armNotSale, setArmNotSale] = useState(false);
  const { data, setData, options, load } = useSalesIntel(legacyId, open);
  useEffect(() => {
    if (["Sale", "Swim Spa"].includes(taskType)) {
      api.get("/sales-intel/status").then((r) => setOn(!!r.data.enabled)).catch(() => setOn(false));
    }
  }, [taskType]);
  if (!on) return null;
  const save = (field, value) => api.post(`/sales-intel/jobs/${legacyId}/confirmations`, { field, value })
    .then((r) => { setData(r.data); setEditing(null); toast.success("Saved to Sales Intelligence (job unchanged)"); })
    .catch((e) => toast.error(e.response?.data?.detail || "Could not save"));
  const revoke = (id) => api.delete(`/sales-intel/confirmations/${id}`).then(() => { load(); toast.success("Confirmation undone"); })
    .catch((e) => toast.error(e.response?.data?.detail || "Could not undo"));
  const gaps = data?.fields?.filter((f) => f.state === "unknown").length || 0;
  return (
    <section className="mt-2 rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="sales-intel-card">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-2 px-4 py-3 text-left" data-testid="sales-intel-toggle">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <Sparkles className="w-4 h-4 text-indigo-500" />
        <span className="text-sm font-semibold text-slate-800">Sales intelligence</span>
        <span className="text-xs text-slate-400 hidden sm:inline">Read-only · optional</span>
        {open && data && !data.error && <span className="ml-auto text-xs text-slate-500 whitespace-nowrap" data-testid="sales-intel-gap-count">{gaps ? `${gaps} unknown` : "All key details found"}</span>}
      </button>
      {open && (
        <div className="px-4 pb-4" data-testid="sales-intel-body">
          {!data && <div className="flex items-center gap-2 text-sm text-slate-500 py-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
          {data?.error && <p className="text-sm text-slate-500 py-3" data-testid="sales-intel-error">Sales intelligence is not available for this job.</p>}
          {data && !data.error && (<>
            {data.fields.map((f) => <FieldRow key={f.key} field={f} canConfirm={data.can_confirm} editing={editing} setEditing={setEditing} options={options} onSave={save} />)}
            {!!data.woo_lines.length && (
              <div className="mt-3 text-xs text-slate-600" data-testid="si-woo-lines">
                <p className="font-semibold text-slate-500">WooCommerce order lines</p>
                {data.woo_lines.map((l, i) => <p key={i}>{l.name} · {l.class.replace(/_/g, " ")} ({l.basis})</p>)}
              </div>)}
            <Suggestions items={data.install_suggestions} canConfirm={data.can_confirm} onDecide={save} />
            <Confirmations items={data.confirmations} canConfirm={data.can_confirm} onRevoke={revoke} />
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <p className="text-[11px] text-slate-400 flex-1 min-w-[12rem]">{data.note}</p>
              {data.can_confirm && data.stage !== "excluded" && (
                armNotSale ? (
                  <span className="flex items-center gap-2 text-xs" data-testid="si-not-a-sale-confirm-row">
                    <span className="text-slate-600">Exclude this job from sales figures?</span>
                    <button type="button" className="text-rose-600 font-semibold" onClick={() => { setArmNotSale(false); save("not_a_sale", "true"); }}
                      data-testid="si-not-a-sale-confirm">Yes, not a sale</button>
                    <button type="button" className="text-slate-500" onClick={() => setArmNotSale(false)} data-testid="si-not-a-sale-cancel">Cancel</button>
                  </span>
                ) : (
                <button type="button" className="text-xs text-slate-500 hover:text-rose-600" onClick={() => setArmNotSale(true)} data-testid="si-not-a-sale">
                  Mark as not a sale</button>))}
            </div>
          </>)}
        </div>)}
    </section>
  );
};
